import React from 'react'
import styled, { createGlobalStyle } from 'styled-components';
import { Executive } from '../../../Types/PersonTypes';
import InfiniteScroll from 'react-infinite-scroll-component';
import classNames from 'classnames';
import AffinitySearchResultItemSkeleton from '../../common/skeleton/AffinitySearchResultItemSkeleton';
import {
  primaryColor,
  primaryFont,
  primaryHeadingColor,
  scrollbar,
  SecondaryColor,
  secondaryFont,
  smMobile,
  wLight,
  wMedium,
  textBlack,
  greyColor1,
  maxMobile
} from '../../../Utils/utilsStyle';
import AlertsSearch from '../../alerts/components/AlertsSearch';
import { Tooltip } from 'antd';

const Wrapper = styled.div`
  border-radius: 14px;
  background: #FFF;
  height: 275px;
  padding: 0 0 0 8px;
  width: 230px;
  .header-title {
    color: ${primaryColor};
    font-family: ${primaryFont};
    font-size: 18px;
    font-weight: ${wMedium};
    line-height: 34px;
    margin-bottom: 10px;
  }
  .search-query {
    input {
      color: ${textBlack};
      font-family: ${primaryFont};
      font-size: 12px;
      font-weight: ${wLight};
      line-height: 13.565px;
      letter-spacing: -0.35px;
      &::placeholder {
        font-style: italic;
        color: ${greyColor1};
        font-weight: ${wLight};
      }
    }
    .ant-input-prefix {
      margin-right: 5px;
      padding-right: 0;
      padding-left: 0;
      margin-left: 0;
    }
    @media all and (max-width: ${smMobile}) {
      width: 100%;
      justify-content: flex-end;
      margin-top: 10px;
    }
  }
  .search-wrapper {
    .cross-icon {
      top: 7px;
      @media all and (min-width: ${smMobile}) and (max-width: ${maxMobile}) {
        top: 10px;
      }
      @media (max-width: calc(${smMobile} - 1px)) {
        top: 17px;
      }
    }
    svg {
      height: 14px;
      margin-left: -5px;
    } 
  }
  .people-content {
    height: calc(100% - 75px);
    cursor: pointer;
    ${scrollbar}
    @media all and (max-width: ${maxMobile}) {
      height: calc(100% - 85px);
    }
    &::-webkit-scrollbar-track {
      background: transparent
    }
    &::-webkit-scrollbar {
      width: 5px;
      border-radius:8px;
    }
    &::-webkit-scrollbar-thumb {
      background: ${SecondaryColor};
      border-radius: 12px;
    }
    .infinite-scroll-component__outerdiv {
      .infinite-scroll-component {
        display: flex;
        flex-direction: column;
        row-gap: 15px;
      }
    }
    .person-info {
      display: flex;
      align-items: flex-start;
      column-gap: 10px;
      .disabled {
        opacity: 0.5;
      }
      &.disabled {
        pointer-events: auto;
      }
      .person-details {
        display: flex;
        flex-direction: column;
        row-gap: 5px;
        max-width: 150px;
        .name {
          color: #000;
          font-family: ${secondaryFont};
          font-size: 14px;
          font-weight: ${wMedium};
          line-height: 15px;
        }
        .designation {
          color: ${primaryHeadingColor};
          font-family: ${primaryFont};
          font-size: 10px;
          font-weight: ${wMedium};
          line-height: 9px;
        }
        .company {
          color: ${primaryColor};
          font-family: ${primaryFont};
          font-size: 10px;
          font-weight: ${wMedium};
          line-height: 9px;
        }
      }
    }
  }
  .empty-list {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #000;
    font-family: ${secondaryFont};
    font-size: 14px;
    font-weight: ${wMedium};
    line-height: 15px;
  }
  .skeleton {
    width: 220px;
    margin-left: -18px;
  }
`;
const TooltipStyles = createGlobalStyle`
  .person-designation-tip {
    .ant-tooltip-inner {
      max-width: 250px;
      white-space: normal;
      font-weight: ${wMedium};
      color: #424b50;
      font-size: 14px;
      font-family: ${primaryFont};
      padding: 10px;
      margin-left: -45px;
      @media all and (max-width: ${maxMobile}) {
        font-size: 12px;
      }
      @media all and (max-width: ${maxMobile}) {
          font-size: 12px;
      }
    }
  }
`;
const ImageWrapper = styled.div<{ color: string }>`
  background: conic-gradient(from -70deg, ${(props) => props.color});
  width: 45px;
  height: 45px;
  border-radius: 50%;
  padding: 2px;
`;
const Image = styled.div<{ image: string }>`
  width: 100%;
  height: 100%;
  border-radius: 50px;
  background-image: url(${(props) => props.image});
  background-position:center;
  background-size:cover;
`;
type Props = {
  staff: Executive[];
  disabledIds: number[];
  loading: boolean,
  totalStaff: number;
  onSelect: (personId: number) => void;
  onFetchMore: () => void;
  onSearching?: (query: string) => void;
  query?: string;
  scrollContainerId?: string;
};
const defaultProps = {
  staff: [] as Executive[],
  disabledIds: [] as number[],
  loading: false,
  totalStaff: 0,
  query: '',
  scrollContainerId: 'scrollPeople',
};
const AlignmentPopoverList = function AlignmentPopoverList(props: Props) {
  const {
    staff,
    disabledIds,
    loading,
    totalStaff,
    onSelect,
    onFetchMore,
    onSearching,
    scrollContainerId,
  } = props;
  return (
    <Wrapper className='popover-container'>
      <TooltipStyles />
      <div className='header-title'>
        People {" "}
        {
          totalStaff >= 0 && (
            <span>{`(${totalStaff})`}</span>
          )
        }
        <AlertsSearch
          placeholderText="Enter person name here"
          customClass="search-query"
          loading={loading}
          onSearching={onSearching ? onSearching : () => { }}
          query={props.query}
          enableQueryTrim={true}
          margin="0 8px 0 0"
          height="28px"
        />
      </div>
      <div id={scrollContainerId} className='people-content'>
        {
          staff?.length ? (
            <InfiniteScroll
              scrollableTarget={scrollContainerId as string}
              hasMore={staff.length < totalStaff}
              dataLength={staff.length}
              next={onFetchMore}
              loader={false}
              className="o-x-hidden o-y-hidden"
            >
              {
                staff.map((person) => {
                  const isDisabled = disabledIds.includes(person.personId);
                  return (
                    <div
                      key={person.personId}
                      className={classNames("person-info", {
                        disabled: isDisabled
                      })}
                      onClick={() => {
                        if (!isDisabled) {
                          onSelect(person.personId);
                        }
                      }}
                    >
                      <ImageWrapper color={person.personalityTypes?.[0]?.color}>
                        <Image image={person.personImage} />
                      </ImageWrapper>
                      <div className='person-details'>
                        <span className='name'>{person.personName}</span>
                        <Tooltip
                          trigger="hover"
                          placement="bottom"
                          getPopupContainer={() => document.getElementById(scrollContainerId as string) || document.body}
                          overlayClassName="person-designation-tip"
                          color="#fff"
                          title={person.personDesignation}
                          arrow={false}
                          autoAdjustOverflow={false}
                        >
                          <div className='designation text-truncate-two-line'>
                            {person.personDesignation}
                          </div>
                        </Tooltip>
                        <span className='company'>{person.companyName}</span>
                      </div>

                    </div>
                  );
                })
              }
            </InfiniteScroll>
          ) : !loading && (
            <div className='empty-list'>No People Found</div>
          )
        }
        {
          loading && (
            <div className="skeleton">
              {[1, 2, 3, 4].map(() => <AffinitySearchResultItemSkeleton />)}
            </div>
          )
        }

      </div>
    </Wrapper>
  );
};
AlignmentPopoverList.defaultProps = defaultProps;
export default AlignmentPopoverList;